import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import { ComponentProps, forwardRef } from 'react';
import { Skeleton } from 'v5/design-system/Skeleton';
import { cn } from 'v5/platform/dom/cn';
import { CrossfadeLayers } from 'v5/primitives/CrossfadeLayers';
import { FadeInImage } from 'v5/primitives/FadeInImage';

export interface EntityHeaderImageProps extends ComponentProps<'div'> {
  imageUrl: RemoteData<Maybe<string>>;
  fallbackContent?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

export const EntityHeaderImage = forwardRef<
  HTMLDivElement,
  EntityHeaderImageProps
>(({ imageUrl, fallbackContent, disabled, ...props }, ref) => {
  const onClick = disabled ? undefined : props.onClick;

  return (
    <div
      ref={ref}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn(
        'group',
        'size-[100px] group rounded-4xl relative overflow-hidden shadow-sm shadow-black/20 bg-bg-default',
        props.className
      )}
      onClick={onClick}
      {...props}
    >
      <CrossfadeLayers
        layerKey={
          imageUrl.status === 'success'
            ? maybe.mapOrElse(imageUrl.data, url => `src:${url}`, 'fallback')
            : imageUrl.status
        }
        className="size-full"
      >
        {rd
          .journey(imageUrl)
          .wait(<Skeleton className="size-full" />)
          .catch(error => (
            <div className="size-full bg-bg-subtle-2 flex items-center justify-center">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-fg-muted"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.3"
                />
                <path
                  d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
                  fill="currentColor"
                />

                <path
                  d="M15 9L9 15M9 9L15 15"
                  // stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ))
          .map(imageUrl =>
            maybe.mapOrElse(
              imageUrl,
              imageUrl => (
                <FadeInImage
                  src={imageUrl}
                  alt="Profile"
                  className={cn(
                    'size-full object-cover ring-8 ring-white ring-inset',
                    onClick &&
                      'transition-all ease-in-out group-hocus:brightness-90'
                  )}
                />
              ),
              <div
                className={cn(
                  'size-full flex items-center justify-center bg-bg-subtle text-3xl text-fg-muted',
                  onClick &&
                    'transition-all ease-in-out group-hocus:brightness-90'
                )}
              >
                {fallbackContent}
              </div>
            )
          )}
      </CrossfadeLayers>

      <div className="absolute size-full inset-0 ring-1 ring-white/40 ring-inset rounded-4xl" />
    </div>
  );
});
